import { Fragment, useEffect, useState } from 'react';
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Transition,
  Dialog,
  DialogPanel,
  TransitionChild,
} from '@headlessui/react';
import { clsx } from 'clsx';
import {
  UserCircleIcon,
  PowerIcon,
  Bars3Icon,
  ChevronDownIcon,
  UserIcon,
  XMarkIcon,
  HomeIcon,
  DocumentTextIcon,
  WrenchScrewdriverIcon,
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { NavLink } from 'react-router-dom';
import { useCurrentUser } from '@/shared/hooks/useCurrentUser';
import { logout } from '@/lib/auth';
import { HeaderSearch } from './HeaderSearch';
import { NotificationsDropdown } from '@/shared/components/NotificationsDropdown';
import { UserRole, hasRole } from '@/types/auth';
import { isMockAuthEnabled } from '@/lib/auth';

const ROLE_SIMULATOR_KEY = 'ai-registry-role-simulator';
const ORIGINAL_ADMIN_KEY = 'ai-registry-original-admin';

interface INavItem {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  roles?: UserRole[];
  excludeRoles?: UserRole[];
}

const navigation: INavItem[] = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  {
    name: 'Use Cases',
    href: '/use-cases',
    icon: DocumentTextIcon,
    excludeRoles: [UserRole.IT_TEAM],
  },
  { name: 'AI Tools', href: '/ai-tools', icon: WrenchScrewdriverIcon },
  {
    name: 'Approvals',
    href: '/approvals',
    icon: ClipboardDocumentCheckIcon,
    roles: [
      UserRole.AI_CHAMPION,
      UserRole.AI_COUNCIL,
      UserRole.IT_TEAM,
      UserRole.EXECUTIVE_SPONSOR,
      UserRole.ADMIN,
    ],
  },
  {
    name: 'Reports',
    href: '/reports',
    icon: ChartBarIcon,
    // Must match the /reports route allowedRoles in App.tsx
    roles: [UserRole.AI_CHAMPION, UserRole.AI_COUNCIL, UserRole.ADMIN],
  },
  {
    name: 'Admin',
    href: '/admin',
    icon: Cog6ToothIcon,
    roles: [UserRole.ADMIN],
  },
];

function getRoleDisplayLabel(role: UserRole): string {
  switch (role) {
    case UserRole.END_USER:
      return 'User';
    case UserRole.AI_CHAMPION:
      return 'AI Champion';
    case UserRole.AI_COUNCIL:
      return 'AI Council';
    case UserRole.IT_TEAM:
      return 'IT Team';
    case UserRole.EXECUTIVE_SPONSOR:
      return 'Executive Sponsor';
    case UserRole.ADMIN:
      return 'Admin';
    default:
      return String(role).replace('AI-Registry-', '');
  }
}

const allRoles: Array<{ value: UserRole | null; label: string }> = [
  { value: null, label: 'Admin' },
  { value: UserRole.END_USER, label: 'User' },
  { value: UserRole.AI_CHAMPION, label: 'AI Champion' },
  { value: UserRole.AI_COUNCIL, label: 'AI Council' },
  { value: UserRole.IT_TEAM, label: 'IT Team' },
  { value: UserRole.EXECUTIVE_SPONSOR, label: 'Exec Sponsor' },
];

export function Header() {
  const { user } = useCurrentUser();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showRolePicker, setShowRolePicker] = useState(false);
  const [simulatedRole, setSimulatedRole] = useState<UserRole | null>(() => {
    try {
      const saved = localStorage.getItem(ROLE_SIMULATOR_KEY);
      if (saved && Object.values(UserRole).includes(saved as UserRole)) {
        return saved as UserRole;
      }
    } catch {
      // Ignore errors
    }
    return null;
  });
  const [isOriginalAdmin, setIsOriginalAdmin] = useState(() => {
    try {
      return localStorage.getItem(ORIGINAL_ADMIN_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const shouldShowRoleSimulator = import.meta.env.DEV || isMockAuthEnabled;
  const isAdmin = user?.roles.includes(UserRole.ADMIN) || false;

  useEffect(() => {
    const storedOriginalAdmin = localStorage.getItem(ORIGINAL_ADMIN_KEY);

    if (storedOriginalAdmin === 'true') {
      setIsOriginalAdmin(true);
    } else {
      if (isAdmin || shouldShowRoleSimulator) {
        localStorage.setItem(ORIGINAL_ADMIN_KEY, 'true');
        setIsOriginalAdmin(true);
      } else {
        setIsOriginalAdmin(false);
      }
    }

    if (shouldShowRoleSimulator || isAdmin || storedOriginalAdmin === 'true') {
      const saved = localStorage.getItem(ROLE_SIMULATOR_KEY);
      if (saved) {
        try {
          const role = saved as UserRole;
          if (Object.values(UserRole).includes(role)) {
            setSimulatedRole(role);
          }
        } catch {
          // Invalid saved value, ignore
        }
      }
    }
  }, [shouldShowRoleSimulator, user?.roles, isAdmin]);

  const handleLogout = () => {
    void logout();
  };

  const handleRoleChange = (role: UserRole | null) => {
    if (!localStorage.getItem(ORIGINAL_ADMIN_KEY)) {
      if (isOriginalAdmin || shouldShowRoleSimulator || isAdmin) {
        localStorage.setItem(ORIGINAL_ADMIN_KEY, 'true');
        setIsOriginalAdmin(true);
      }
    }
    setSimulatedRole(role);
    if (role) {
      localStorage.setItem(ROLE_SIMULATOR_KEY, role);
    } else {
      localStorage.removeItem(ROLE_SIMULATOR_KEY);
    }
    window.location.reload();
  };

  const shouldShowSwitchView = isAdmin || shouldShowRoleSimulator || isOriginalAdmin;

  const activeRoleLabel = simulatedRole
    ? allRoles.find((r) => r.value === simulatedRole)?.label || 'Simulated'
    : getRoleDisplayLabel(user?.roles[0] ?? UserRole.END_USER);

  const visibleNavItems = navigation.filter((item) => {
    if (item.excludeRoles && hasRole(user, item.excludeRoles) && !hasRole(user, [UserRole.ADMIN])) {
      return false;
    }
    if (!item.roles) return true;
    return hasRole(user, item.roles);
  });

  return (
    <>
      <header className="bg-white border-b border-gray-200">
        <div className="flex items-center h-14 px-4 gap-2">
          {/* Mobile menu button */}
          <button
            type="button"
            className="lg:hidden p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={() => { setMobileMenuOpen(true); }}
            aria-label="Open menu"
          >
            <Bars3Icon className="w-6 h-6" />
          </button>

          {/* Logo */}
          <NavLink to="/" className="flex items-center gap-2 flex-shrink-0 mr-1">
            <img
              src="/images/corus-logo-490.png"
              alt="Corus International"
              className="h-8 w-8 rounded object-contain"
            />
            <span className="hidden sm:block font-display text-base font-semibold tracking-tight text-gray-900">
              AI Registry
            </span>
          </NavLink>

          {/* Desktop nav links */}
          <nav className="hidden lg:flex items-center gap-0.5 flex-shrink-0">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                end={item.href === '/'}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap',
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  )
                }
              >
                <item.icon className="w-4 h-4" />
                {item.name}
              </NavLink>
            ))}
          </nav>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Search */}
          <div className="w-[280px] max-w-[360px] flex-shrink min-w-[120px]">
            <HeaderSearch />
          </div>

          {/* Notifications */}
          <NotificationsDropdown />

          {/* User menu */}
          <Menu as="div" className="relative flex-shrink-0">
            <MenuButton className="flex items-center gap-2 p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-900 leading-tight">
                  {user?.name || 'User'}
                </p>
                <p className="text-xs text-gray-500 leading-tight">{activeRoleLabel}</p>
              </div>
              <UserCircleIcon className="w-8 h-8 text-gray-400" />
            </MenuButton>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems className="absolute right-0 z-30 mt-2 w-64 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="p-2">
                  {/* Profile info */}
                  <div className="px-3 py-3 border-b border-gray-100 mb-1">
                    <p className="text-sm font-semibold text-gray-900">
                      {user?.name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{user?.email}</p>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <UserIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span>{activeRoleLabel}</span>
                      </div>
                    </div>
                  </div>

                  {/* Switch View accordion */}
                  {shouldShowSwitchView && (
                    <>
                      <button
                        type="button"
                        className={clsx(
                          'flex items-center justify-between w-full px-3 py-2 text-sm rounded-md',
                          showRolePicker
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-700 hover:bg-gray-100'
                        )}
                        onClick={() => {
                          setShowRolePicker(!showRolePicker);
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <UserIcon className="w-4 h-4" />
                          <span>Switch View</span>
                        </div>
                        <ChevronDownIcon
                          className={clsx(
                            'w-3.5 h-3.5 text-gray-400 transition-transform',
                            showRolePicker && 'rotate-180'
                          )}
                        />
                      </button>
                      {showRolePicker && (
                        <div className="pl-3 pr-1 py-1">
                          {allRoles.map((role) => {
                            const isActive = simulatedRole === role.value;
                            return (
                              <button
                                key={role.value || 'none'}
                                type="button"
                                onClick={() => { handleRoleChange(role.value); }}
                                className={clsx(
                                  'flex items-center gap-2 w-full px-3 py-1.5 text-sm rounded-md text-left transition-colors',
                                  isActive
                                    ? 'bg-primary-50 text-primary-700 font-medium'
                                    : 'text-gray-600 hover:bg-gray-50'
                                )}
                              >
                                {isActive && (
                                  <span className="text-primary-600 text-xs">✓</span>
                                )}
                                {!isActive && (
                                  <span className="w-3 text-xs" />
                                )}
                                <span>{role.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      <div className="border-t border-gray-100 my-1" />
                    </>
                  )}

                  {/* Sign out */}
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        onClick={handleLogout}
                        className={clsx(
                          'flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md',
                          focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                        )}
                      >
                        <PowerIcon className="w-5 h-5" />
                        Sign out
                      </button>
                    )}
                  </MenuItem>
                </div>
              </MenuItems>
            </Transition>
          </Menu>
        </div>
      </header>

      {/* Mobile navigation drawer */}
      <Transition show={mobileMenuOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50 lg:hidden"
          onClose={() => { setMobileMenuOpen(false); }}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 left-0 flex max-w-full pr-10">
                <TransitionChild
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300"
                  enterFrom="-translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300"
                  leaveFrom="translate-x-0"
                  leaveTo="-translate-x-full"
                >
                  <DialogPanel className="pointer-events-auto w-screen max-w-xs">
                    <div className="flex h-full flex-col overflow-y-auto bg-white shadow-xl">
                      <div className="flex items-center justify-between h-14 px-4 border-b border-gray-200">
                        <div className="flex items-center gap-2">
                          <img
                            src="/images/corus-logo-490.png"
                            alt="Corus International"
                            className="h-8 w-8 rounded object-contain"
                          />
                          <div>
                            <p className="text-sm font-semibold text-gray-900">AI Registry</p>
                            <p className="text-xs text-gray-500">Corus International</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="rounded-md text-gray-400 hover:text-gray-500"
                          onClick={() => { setMobileMenuOpen(false); }}
                        >
                          <span className="sr-only">Close menu</span>
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>

                      <nav className="flex-1 px-3 py-4 space-y-1">
                        {visibleNavItems.map((item) => (
                          <NavLink
                            key={item.name}
                            to={item.href}
                            end={item.href === '/'}
                            onClick={() => { setMobileMenuOpen(false); }}
                            className={({ isActive }) =>
                              clsx(
                                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                                isActive
                                  ? 'bg-primary-50 text-primary-700'
                                  : 'text-gray-700 hover:bg-gray-100'
                              )
                            }
                          >
                            <item.icon className="w-5 h-5" />
                            {item.name}
                          </NavLink>
                        ))}
                      </nav>
                    </div>
                  </DialogPanel>
                </TransitionChild>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
